import {
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  setDoc,
  where,
  type Timestamp,
} from 'firebase/firestore';
import { appConfig } from '@/config/app';
import { getFirestoreDb } from '@/services/firebase';
import {
  buildTimestampPayload,
  timestampToDate,
} from '@/modules/inventory/services/firestoreMapper';
import { LOGISTICS_PROVIDERS_COLLECTION } from '@/modules/logistics/services/constants';
import type {
  LogisticsProvider,
  LogisticsProviderInput,
} from '@/modules/logistics/types';
import { recordEntityWrite, toAuditSnapshot } from '@/services/audit';
import { withFirestoreError } from '@/shared/utils/firestoreError';

type ProviderFirestoreDoc = {
  companyId: string;
  name: string;
  description: string;
  contactName: string;
  contactPhone: string;
  email: string;
  active: boolean;
  createdAt: Timestamp;
  updatedAt: Timestamp;
};

function mapProvider(id: string, data: ProviderFirestoreDoc): LogisticsProvider {
  return {
    id,
    companyId: data.companyId,
    name: data.name,
    description: data.description ?? '',
    contactName: data.contactName ?? '',
    contactPhone: data.contactPhone ?? '',
    email: data.email ?? '',
    active: data.active,
    createdAt: timestampToDate(data.createdAt),
    updatedAt: timestampToDate(data.updatedAt),
  };
}

export async function listLogisticsProviders(
  companyId: string = appConfig.companyId,
): Promise<LogisticsProvider[]> {
  const q = query(
    collection(getFirestoreDb(), LOGISTICS_PROVIDERS_COLLECTION),
    where('companyId', '==', companyId),
  );
  const snap = await getDocs(q);
  return snap.docs
    .map((d) => mapProvider(d.id, d.data() as ProviderFirestoreDoc))
    .sort((a, b) => a.name.localeCompare(b.name));
}

export async function getLogisticsProvider(
  id: string,
): Promise<LogisticsProvider | null> {
  const snap = await getDoc(
    doc(getFirestoreDb(), LOGISTICS_PROVIDERS_COLLECTION, id),
  );
  if (!snap.exists()) return null;
  return mapProvider(snap.id, snap.data() as ProviderFirestoreDoc);
}

export async function saveLogisticsProvider(
  input: LogisticsProviderInput,
  id?: string,
): Promise<LogisticsProvider> {
  return withFirestoreError(async () => {
  const ref = id
    ? doc(getFirestoreDb(), LOGISTICS_PROVIDERS_COLLECTION, id)
    : doc(collection(getFirestoreDb(), LOGISTICS_PROVIDERS_COLLECTION));
  const existing = await getDoc(ref);
  const before = existing.exists()
    ? toAuditSnapshot(
        mapProvider(existing.id, existing.data() as ProviderFirestoreDoc),
      )
    : undefined;
  const payload = {
    companyId: appConfig.companyId,
    name: input.name.trim(),
    description: input.description.trim(),
    contactName: input.contactName.trim(),
    contactPhone: input.contactPhone.trim(),
    email: input.email.trim(),
    active: input.active,
    ...buildTimestampPayload(!existing.exists()),
  };
  await setDoc(ref, payload, { merge: true });
  const saved = await getDoc(ref);
  if (!saved.exists()) throw new Error('No se pudo guardar el proveedor.');
  const result = mapProvider(saved.id, saved.data() as ProviderFirestoreDoc);
  recordEntityWrite({
    entityType: 'logisticsProvider',
    entityId: result.id,
    isCreate: !existing.exists(),
    ...(before !== undefined ? { before } : {}),
    after: toAuditSnapshot(result),
  });
  return result;
  }, 'logisticsProvider');
}
